import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { Loader2, Sparkles, RefreshCw } from "lucide-react";

export function HR360() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/hr/overview").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="HR360 · Human Capital"
                title="Organisation · Competency · People Analytics"
                description="People are governed with the same rigour as enterprise assets — competencies, medicals, training, succession, performance and fatigue management." />
            <KpiGrid kpis={data?.kpis || []} accent="purple" cols={4} />
            <Panel title="Employee Register" accent="purple">
                <DataTable
                    columns={[
                        { key: "employee_no", label: "ID" },
                        { key: "name", label: "Name" },
                        { key: "department", label: "Dept" },
                        { key: "position", label: "Position" },
                        { key: "years_service", label: "Yrs" },
                        { key: "competency_pct", label: "Competency", render: (r) => `${r.competency_pct}%` },
                        { key: "certifications", label: "Certifications", render: (r) => r.certifications.slice(0,3).join(", ") },
                        { key: "medical_valid", label: "Medical", render: (r) => <StatusChip status={r.medical_valid ? "normal" : "warning"}>{r.medical_valid ? "Valid" : "Expired"}</StatusChip> },
                        { key: "fitness_for_work", label: "Fitness", render: (r) => <StatusChip status={r.fitness_for_work === "Fit" ? "normal" : "warning"}>{r.fitness_for_work}</StatusChip> },
                        { key: "performance", label: "Performance", render: (r) => <StatusChip status={statusToChip(r.performance)}>{r.performance}</StatusChip> },
                        { key: "training_hours_ytd", label: "Training h" },
                        { key: "succession_ready", label: "Successor?", render: (r) => r.succession_ready ? <StatusChip status="normal">Ready</StatusChip> : "—" },
                    ]}
                    rows={(data?.employees || []).slice(0, 40)}
                    testid="hr-table"
                />
            </Panel>
        </div>
    );
}

export function ValueRealisation() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/value/overview").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Value Realisation Centre" title="Measurable Business Value Delivered by KCC360"
                description="Every digital initiative is measured against a target with independent assurance. Nothing is claimed as value until it is realised." />
            <KpiGrid kpis={data?.kpis || []} accent="green" cols={4} />
            <Panel title="Value Realised by Initiative" accent="green">
                <DataTable
                    columns={[
                        { key: "initiative", label: "Initiative" },
                        { key: "area", label: "Area" },
                        { key: "target_musd", label: "Target $M" },
                        { key: "actual_musd", label: "Actual $M" },
                        { key: "roi_pct", label: "ROI", render: (r) => `${r.roi_pct}%` },
                        { key: "confidence", label: "Confidence", render: (r) => <StatusChip status={statusToChip(r.confidence)}>{r.confidence}</StatusChip> },
                        { key: "assurance", label: "Assurance" },
                        { key: "evidence", label: "Evidence" },
                        { key: "quarter", label: "Quarter" },
                    ]}
                    rows={data?.items || []}
                    testid="value-table"
                />
            </Panel>
        </div>
    );
}

export function StrategyCentre() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/strategy/overview").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Enterprise Strategy Centre" title="Corporate Strategy · Transformation · Decision Register"
                description="Strategic initiatives, corporate KPIs, transformation programmes and every executive decision — with AI recommendation and outcome tracking." />
            <KpiGrid kpis={data?.kpis || []} accent="purple" cols={5} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Strategic Initiatives" accent="purple">
                    <DataTable
                        columns={[
                            { key: "name", label: "Initiative" },
                            { key: "pillar", label: "Pillar" },
                            { key: "sponsor", label: "Sponsor" },
                            { key: "progress_pct", label: "Progress", render: (r) => <div><div className="text-xs text-white">{r.progress_pct}%</div><div className="w-24 h-1.5 bg-white/10 rounded"><div className="h-1.5 rounded bg-gradient-to-r from-kcc-copper to-kcc-cobalt-light" style={{ width: `${r.progress_pct}%` }} /></div></div> },
                            { key: "benefit_musd", label: "Benefit $M" },
                            { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        ]}
                        rows={data?.initiatives || []}
                        testid="strategy-inits"
                    />
                </Panel>
                <Panel title="Executive Decision Register" accent="cobalt">
                    <DataTable
                        columns={[
                            { key: "ref", label: "Ref" },
                            { key: "title", label: "Decision" },
                            { key: "owner", label: "Owner" },
                            { key: "date", label: "Date", render: (r) => r.date.slice(0, 10) },
                            { key: "ai_recommendation", label: "AI Rec" },
                            { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                            { key: "benefit_musd", label: "Benefit $M" },
                        ]}
                        rows={data?.decisions || []}
                        testid="strategy-decisions"
                    />
                </Panel>
            </div>
        </div>
    );
}

export function SupplierIntelligence() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/suppliers/intelligence").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Supplier Performance Intelligence"
                title="Ranked Suppliers · Preferred · Watch · Review"
                description="Composite scoring by quality, delivery, lead time, warranty, safety, ESG, price and risk. AI recommends preferred suppliers automatically." />
            <KpiGrid kpis={data?.kpis || []} accent="cobalt" cols={4} />
            <Panel title="Supplier Scorecard" accent="cobalt">
                <DataTable
                    columns={[
                        { key: "supplier", label: "Supplier" },
                        { key: "composite", label: "Composite", render: (r) => <span className={r.composite >= 90 ? "text-kcc-green font-mono-kcc" : r.composite >= 80 ? "text-kcc-amber font-mono-kcc" : "text-kcc-red font-mono-kcc"}>{r.composite}</span> },
                        { key: "quality_score", label: "Quality" },
                        { key: "delivery_score", label: "Delivery" },
                        { key: "lead_time_days", label: "Lead (d)" },
                        { key: "warranty_claims", label: "Warranty" },
                        { key: "safety_score", label: "Safety" },
                        { key: "esg_score", label: "ESG" },
                        { key: "risk_score", label: "Risk", render: (r) => <span className={r.risk_score >= 4 ? "text-kcc-red" : r.risk_score >= 3 ? "text-kcc-amber" : "text-kcc-green"}>{r.risk_score}/5</span> },
                        { key: "recommendation", label: "Recommendation", render: (r) => <StatusChip status={statusToChip(r.recommendation)}>{r.recommendation}</StatusChip> },
                    ]}
                    rows={data?.suppliers || []}
                    testid="supplier-scorecard"
                />
            </Panel>
        </div>
    );
}

export function MineLife() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/mine-life/overview").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Mine Life Intelligence"
                title="Life-of-Mine · Reserves · Capital · Closure"
                description="15-year forward forecast of reserves, ore mined, grade, capex, sustaining capex and closure provision." />
            <KpiGrid kpis={data?.kpis || []} accent="copper" cols={4} />
            <Panel title="Life-of-Mine Schedule" accent="copper">
                <DataTable
                    columns={[
                        { key: "year", label: "Year" },
                        { key: "reserve_mt", label: "Reserve (Mt)" },
                        { key: "ore_mined_mt", label: "Ore Mined (Mt)" },
                        { key: "cu_grade_pct", label: "Cu %" },
                        { key: "co_grade_pct", label: "Co %" },
                        { key: "capex_musd", label: "Capex $M" },
                        { key: "sustaining_capex_musd", label: "Sustaining $M" },
                        { key: "closure_provision_musd", label: "Closure $M" },
                    ]}
                    rows={data?.years || []}
                    testid="mine-life-table"
                />
            </Panel>
        </div>
    );
}

export function ExecutiveBriefing() {
    const [reply, setReply] = useState("");
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(false);

    async function generate() {
        setLoading(true); setReply(""); setSources([]);
        try {
            const { data } = await api.post("/executive/briefing", {});
            setReply(data.briefing || ""); setSources(data.sources || []);
        } catch (e) { setReply(`⚠︎ ${e.response?.data?.detail || e.message}`); }
        finally { setLoading(false); }
    }
    useEffect(() => { generate(); }, []);

    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Executive Daily Briefing™"
                title="Your AI-generated briefing for today"
                description="One-page executive briefing — overnight events, critical risks, production, safety, finance, decisions required and recommended next actions. Grounded in live KCC360 data across every workspace."
                actions={<button onClick={generate} disabled={loading} className="inline-flex items-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light text-white rounded-md px-4 py-2 text-sm" data-testid="regenerate-briefing">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate</button>}
            />
            <Panel title="Today's Executive Briefing" accent="purple">
                {loading && !reply && <div className="flex items-center gap-2 kcc-text-silver"><Loader2 className="w-4 h-4 animate-spin" /> Assembling your briefing from every workspace…</div>}
                {reply && <div className="whitespace-pre-wrap text-sm leading-relaxed text-kcc-silver" data-testid="briefing-text">{reply}</div>}
                {sources.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-white/5 flex flex-wrap items-center gap-2 text-[10.5px] kcc-text-dim">
                        <Sparkles className="w-3 h-3 text-kcc-purple" /> Grounded in:
                        {sources.map((s) => <span key={s} className="px-1.5 py-0.5 rounded border border-white/10">{s}</span>)}
                    </div>
                )}
            </Panel>
        </div>
    );
}
